'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { shouldHandleInternalNavigation } from '@/lib/internal-navigation';

function getRoutableHref(anchor: HTMLAnchorElement): string | null {
  if (anchor.target && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download')) return null;
  if (anchor.dataset.hardNavigation === 'true') return null;

  const href = anchor.getAttribute('href');
  if (!href || !shouldHandleInternalNavigation(href, window.location.origin)) return null;

  const url = new URL(href, window.location.origin);
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search &&
    url.hash
  ) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function InternalNavigationHandler() {
  const router = useRouter();

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = getRoutableHref(anchor);
      if (!href) return;

      event.preventDefault();
      router.push(href);
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [router]);

  return null;
}
